import api from '@/api';
import * as types from '@/store/types';
import get from 'lodash/get';
import set from 'lodash/set';

import { loadLanguageAsync } from '@/locale';

/* STATIC */
import statuses from '@/store/dictionaries/statuses';
import offerTypes from '@/store/dictionaries/offerTypes';
import keywordTypes from '@/store/dictionaries/keywordTypes';
import parameterTypes from '@/store/dictionaries/parameterTypes';
import modules from '@/store/dictionaries/modules';
import privileges from '@/store/dictionaries/privileges';
import hours from '@/store/dictionaries/hours';
import daysOfWeek from '@/store/dictionaries/daysOfWeek';
import postbackHttpMethods from '@/store/dictionaries/postbackHttpMethods';
import currencies from '@/store/dictionaries/currencies';
import connectionTypes from '@/store/dictionaries/connectionTypes';
import deviceManufacturer from '@/store/dictionaries/deviceManufacturer';
import conversionAdjustmentReasons from '@/store/dictionaries/conversionAdjustmentReasons';
import trafficFilteringLevels from '@/store/dictionaries/trafficFilteringLevels';
import taskStatuses from '@/store/dictionaries/taskStatuses';
import taskTypes from '@/store/dictionaries/taskTypes';
import applicationEventConversionValues from '@/store/dictionaries/applicationEventConversionValues';

const state = {
    config: {},
    apiVersion: null,
    currencies,
    currenciesDict: null,
    statuses,
    parameterTypes,
    offerTypes,
    keywordTypes,
    taskStatuses,
    taskTypes,
    applicationEventConversionValues,
    applicationPostbackMethods: [],
    statusesOnboarding: [],
    countries: [],
    countriesWithAllOption: [],
    categories: [],
    trafficTypes: [],
    partnersTrafficTypes: [],
    partnersTypes: [],
    advertisersDictionary: [],
    partnersDictionary: [],
    onboardingPartnersDictionary: [],
    usersDictionary: [],
    trafficSources: [],
    paymentTerms: [],
    postbackEvents: [], // this becomes legacy after introducing custom eventTypes, TODO: cleanse system from postbackEvents
    eventTypesDict: [],
    eventTypesWithAllOptionDict: [],
    eventTypesExtendedDict: [],
    virtualEventTypes: [],
    timezonesDict: [],
    userRolesDictionary: [],
    trafficSplitterParams: [],
    tagTypes: [],
    modules,
    privileges,
    hours,
    daysOfWeek,
    postbackHttpMethods,
    offers: [],
    capTypes: [],
    iabCategories: [],
    iabCategoryNames: [],
    emailEvents: [],
    connectionTypes,
    deviceManufacturer,
    payoutTypes: [],
    ISPsDictionary: [],
    currenciesDictTimestampMinutes: 0,
    eventTypesDictTimestampMinutes: 0,
    eventTypesExtendedDictTimestampMinutes: 0,
    activityLogOperations: [],
    fraudReasons: [],
    splittersTypes: [],
    conversionAdjustmentReasons,
    deviceTypes: [],
    advertiserPostbackEvents: [],
    impressionTrackingParams: [],
    osDict: [],
    postbackReportStatuses: [],
    eventStatusChangeTypes: [],
    trafficFilteringLevels,
    approvalEventStatuses: [],
    eventErrorCodes: [],
    advertiserPostbackStatuses: [],
    clickDataTypes: [],
    trackingParams: [],
    clicktagParams: [],
    whitelistIpTypes: [],
    advertiserGeneratorParams: [],
    offerCreatedTimeRange: [],
    analyticsKPITypes: [],
    extCampaignStatuses: [],
    extCampaignTypes: [],
    revenuesGroups: [],
    postbackBodyTypes: [],
    foreignLeadSources: [],
};


const getters = {
    config:                            state => state.config,
    defaultEventType:                  state => state.config.general && state.config.general.default_event_type,
    isDCBEnabled:                      state => state.config.general && state.config.general.dcb.enabled,
    systemName:                        state => state.config.general && state.config.general.system_name,
    isCustomTrackingParamsEnabled:     state => state?.config?.feature?.custom_tracking_params?.enabled,
    isPendingStatusEnabled:            state => state?.config?.feature?.pending_status?.enabled,
    isSKAdNetworkAvailable:            state => state?.config?.feature?.skadnetwork?.enabled,
    isTrafficSplitAvailable:           state => state?.config?.feature?.traffic_split?.enabled,
    isTSPAutoCapsEnabled:              state => state?.config?.feature?.auto_tsp_caps?.enabled,
    isEventErrorStatusEnabled:         state => state?.config?.feature?.event_error_status?.enabled,
    isCapsLimitationEnabled:           state => state?.config?.feature?.caps_limitation?.enabled,
    isBillingExtraFieldsEnabled:       state => state?.config?.feature?.billing_extra_fields?.enabled,
    isParameterGeneratorEnabled:       state => state?.config?.feature?.parameter_generator?.enabled,
    isAppsflyerAdvancedPrivacyEnabled: state => state?.config?.feature?.appsflyer_advanced_privacy?.enabled,
    isAppsflyerAudiencesEnabled:       state => state?.config?.feature?.appsflyer_audiences?.enabled,
    isFeatureExtCampaignsEnabled:      state => state?.config?.feature?.ext_campaigns?.enabled,
    isAdvertiserRevenuesGroupEnabled:  state => state?.config?.feature?.advertiser_revenues_group?.enabled,
    isOfferCapParamsEnabled:           state => state?.config?.feature?.offer_cap_params?.enabled,
    isCookiesProcessingEnabled:        state => state?.config?.feature?.cookies_processing?.enabled,
    maxCapsPerParam:                   state => state?.config?.feature?.offer_cap_params?.max_caps_per_param,
    payoutsDefaultPercentage:          state => get(state.config, 'payouts.default_percentage'),
    revenueDefaultPercentage:          state => get(state.config, 'revenue.default_percentage'),
    apiVersion:                        state => state.apiVersion,
    countries:                         state => state.countries,
    countriesWithAllOption:            state => state.countriesWithAllOption,
    categories:                        state => state.categories,
    currenciesDict:                    state => state.currenciesDict,
    daysOfWeek:                        state => state.daysOfWeek,
    trafficTypes:                      state => state.trafficTypes,
    partnersTrafficTypes:              state => state.partnersTrafficTypes,
    partnersTypes:                     state => state.partnersTypes,
    trafficSources:                    state => state.trafficSources,
    hours:                             state => state.hours,
    statuses:                          state => state.statuses,
    statusesOnboarding:                state => state.statusesOnboarding.map(elem => {return { name: elem };}),
    paymentTerms:                      state => state.paymentTerms,
    postbackEvents:                    state => state.postbackEvents,
    postbackHttpMethods:               state => state.postbackHttpMethods,
    postbackBodyTypes:                 state => state.postbackBodyTypes,
    eventTypesDict:                    state => state.eventTypesDict,
    eventTypesWithAllOptionDict:       state => state.eventTypesWithAllOptionDict,
    eventTypesExtendedDict:            state => state.eventTypesExtendedDict,
    virtualEventTypes:                 state => state.virtualEventTypes,
    timezonesDict:                     state => state.timezonesDict,
    parameterTypes:                    state => state.parameterTypes,
    advertisersDictionary:             state => state.advertisersDictionary,
    partnersDictionary:                state => state.partnersDictionary,
    onboardingDictionary:              state => state.onboardingDictionary,
    usersDictionary:                   state => state.usersDictionary,
    userRolesDictionary:               state => state.userRolesDictionary,
    carriers:                          state => state.carriers,
    offerTypes:                        state => state.offerTypes,
    keywordTypes:                      state => state.keywordTypes,
    modules:                           state => state.modules,
    privileges:                        state => state.privileges,
    capTypes:                          state => state.capTypes,
    iabCategories:                     state => state.iabCategories,
    iabCategoryNames:                  state => state.iabCategoryNames,
    connectionTypes:                   state => state.connectionTypes,
    deviceManufacturer:                state => state.deviceManufacturer,
    emailEvents:                       state => state.emailEvents,
    payoutTypes:                       state => state.payoutTypes,
    ISPsDictionary:                    state => state.ISPsDictionary,
    activityLogOperations:             state => state.activityLogOperations,
    fraudReasons:                      state => state.fraudReasons,
    splittersTypes:                    state => state.splittersTypes,
    conversionAdjustmentReasons:       state => state.conversionAdjustmentReasons,
    theme:                             state => state.config?.upyield?.ui?.theme,
    themeSelected:                     state => state.config?.upyield?.ui?.theme?.selected,
    deviceTypes:                       state => state.deviceTypes,
    advertiserPostbackEvents:          state => state.advertiserPostbackEvents,
    impressionTrackingParams:          state => state.impressionTrackingParams,
    osDict:                            state => state.osDict,
    //TODO adjust when api ready (remove manual iOS checking)
    osDictSkad:                        state => state.osDict.filter((os) => ['iOS'].includes(os?.value)),
    applicationEventConversionValues:  state => state.applicationEventConversionValues,
    applicationPostbackMethods:        state => state.applicationPostbackMethods,
    postbackReportStatuses:            state => state.postbackReportStatuses,
    eventStatusChangeTypes:            state => state.eventStatusChangeTypes,
    trafficFilteringLevels:            state => state.trafficFilteringLevels,
    taskStatuses:                      state => state.taskStatuses,
    taskTypes:                         state => state.taskTypes,
    isServerSideClickEnabled:          state => state?.config?.feature?.server_side_click?.enabled,
    isTestToolsEnabled:                 state => state?.config?.feature?.test_tools?.enabled,
    approvalEventStatuses:             state => state.approvalEventStatuses,
    approvalEventStatusesForSettings:  state => state.approvalEventStatuses.filter(status => status.allowed_for_settings),
    eventErrorCodes:                   state => state.eventErrorCodes,
    advertiserPostbackStatuses:        state => state.advertiserPostbackStatuses,
    clickDataTypes:                    state => state.clickDataTypes,
    trackingParams:                    state => state.trackingParams,
    clicktagParams:                    state => state.clicktagParams,
    firstWeekDay:                      state => state?.config?.general?.first_week_day,
    whitelistIpTypes:                  state => state.whitelistIpTypes,
    advertiserGeneratorParams:         state => state.advertiserGeneratorParams,
    offerCreatedTimeRange:             state => state.offerCreatedTimeRange,
    analyticsKPITypes:                 state => state.analyticsKPITypes,
    extCampaignStatuses:               state => state.extCampaignStatuses,
    extCampaignTypes:                  state => state.extCampaignTypes,
    revenuesGroups:                    state => state.revenuesGroups,
    defaultRevenuesGroup:              state => state.config.advertisers && state.config.advertisers.default_revenues_group,
    foreignLeadSources:                state => state.foreignLeadSources,

    privilegeByStringId: state => id => {
        if (!id) return '—';
        const priv = Object.values(state.privileges).find((priv) => priv.string_id === id);
        return priv ? priv.name : id;
    },
    moduleByStringId: state => id => {
        if (!id) return '—';
        const module = Object.values(state.modules).find((module) => module.string_id === id);
        return module ? module.name : `${id} (not found)`;
    },
    paymentTermsNameById: state => id => {
        if (!id) return '—';
        const term = Object.values(state.paymentTerms).find((term) => term.id === id);
        return term ? term.name : `${id} (not found)`;
    },
    trafficSourceNameById: state => id => {
        if (!id) return '—';
        const source = Object.values(state.trafficSources).find((source) => source.id === id);
        return source ? source.name : `${id} (not found)`;
    },
    getCurrency: state => name => {
        const currency = state.currencies.find((currency) => currency.name === name);
        return currency;
    },

    getCurrencySymbol: state => name => {
        const currency = state.currencies.find((currency) => currency.name === name);
        return (currency && currency.symbol) || name;
    },
    revenuesGroupByCode: state => code => {
        if (!code) return '—';
        const source = Object.values(state.revenuesGroups).find((source) => source.code === code);
        return source ? source.name : `${code} (not found)`;
    },
};


const actions = {
    getConfig ({ commit }) {
        return api.config.list().then(({ data }) => {
            const response = data;
            const responseData = data.data;
            return loadLanguageAsync(responseData.general.locale).then(() => {
                responseData.general.first_week_day = Number(responseData.general.first_week_day);
                commit(types.RECEIVE_CONFIG, response);
            });
        });
    },
    getConfigTheme ({ commit }) {
        return api.config.theme().then(({ data }) => {
            commit(types.RECEIVE_CONFIG_THEME, data?.data);
        });
    },
    getEventTypesDict ({ commit }, params = { limit: -1 }) {
        return api.dicts.eventTypes(params).then((response) => {
            commit(types.RECEIVE_EVENT_TYPES_DICT, response.data);
            return response.data;
        });
    },
    getVirtualEventTypes ({ commit }, params = { limit: -1 }) {
        return api.dicts.virtualEventTypes(params).then((response) => {
            commit(types.RECEIVE_VIRTUAL_EVENT_TYPES, response.data);
        });
    },
    getTimezonesDict ({ commit }) {
        return api.dicts.timezones().then((response) => {
            commit(types.RECEIVE_TIMEZONES_DICT, response.data);
        });
    },
    getCategoriesDict ({ commit }) {
        return api.dicts.categories().then((response) => {
            commit(types.RECEIVE_CATEGORIES_DICT, response.data);
        });
    },

    getUsersDictionary ({ commit }, params) {
        return api.users.dict(params).then((response) => {
            commit(types.RECEIVE_USERS_DICTIONARY, response.data);
        });
    },
    getUserRolesDictionary ({ commit }, params) {
        return api.dicts.userRoles(params).then((response) => {
            commit(types.RECEIVE_USER_ROLES_DICTIONARY, response.data);
        });
    },
    getDictionaries ({ commit }) {
        return api.getDicts().then((response) => {
            commit(types.RECEIVE_DICTIONARIES, response);
        });
    },
    getCustomDictionaries ({ commit }) {
        return Promise.all([
            api.dicts.countries(),
            api.dicts.impressionTrackingParams(),
            api.partners.getTrackingParams(),
        ]).then(values => {
            const [
                countries,
                impressionTrackingParams,
                trackingParams,
            ] = values;

            commit(types.RECEIVE_COUNTRIES_DICT, countries.data);
            commit(types.RECEIVE_IMPRESSION_TRACKING_PARAMS_DICT, impressionTrackingParams.data);
            commit(types.RECEIVE_TRACKING_PARAMS_DICT, trackingParams.data);
        });
    },
    getClicktagParamsDict ({ commit }) {
        return api.partners.getClicktagParams()
            .then(({ data: data }) => {
                commit(types.RECEIVE_CLICKTAG_PARAMS_DICT, data);
            });
    },
    getCurrenciesDictionary ({ commit }) {
        return api.dicts.currencies()
            .then(({ data }) => {
                commit(types.RECEIVE_CURRENCIES_DICT, data);
            });
    },
    getCurrenciesDictionaryThrottled ({ commit, state }) {
        const currentTimestampMinutes = Math.floor((new Date()).getTime() / 1000 / 60);

        if (currentTimestampMinutes - state.currenciesDictTimestampMinutes > 4) {
            return api.dicts.currencies()
                .then(({ data }) => {
                    commit(types.RECEIVE_CURRENCIES_DICT, data);
                    commit(types.RECEIVE_CURRENCIES_DICT_TIMESTAMP, currentTimestampMinutes);
                });
        }
    },
    getEventTypesDictionaryThrottled ({ commit, state }) {
        const currentTimestampMinutes = Math.floor((new Date()).getTime() / 1000 / 60);

        if (currentTimestampMinutes - state.eventTypesDictTimestampMinutes > 4) {
            return api.dicts.eventTypes({ sort: 'name' })
                .then(({ data }) => {
                    commit(types.RECEIVE_EVENT_TYPES_DICT, data);
                    commit(types.RECEIVE_EVENT_TYPES_DICT_TIMESTAMP, currentTimestampMinutes);
                });
        }
    },
    getEventTypesExtendedDictionaryThrottled ({ commit, state }) {
        const currentTimestampMinutes = Math.floor((new Date()).getTime() / 1000 / 60);

        if (currentTimestampMinutes - state.eventTypesExtendedDictTimestampMinutes > 4) {
            return api.dicts.eventTypes({ with_system: 1, sort: [ '-system', 'name' ] })
                .then(({ data }) => {
                    commit(types.RECEIVE_EVENT_TYPES_EXTENDED_DICT, data);
                    commit(types.RECEIVE_EVENT_TYPES_EXTENDED_DICT_TIMESTAMP, currentTimestampMinutes);
                });
        }
    },
    getISPsDictionary ({ commit }, params = { limit: 200 }) {
        return api.dicts.isps(params).then((response) => {
            commit(types.RECEIVE_ISPS_DICTIONARY, response.data);
        });
    },
    getFraudReasonsDict ({ commit }) {
        return api.dicts.vettingStrategies()
            .then(({ data }) => {
                commit(types.RECEIVE_FRAUD_REASONS_DICT, data);
            });
    },
    getOsDictionary ({ commit }) {
        return api.tags.tagsOS({ limit: -1 })
            .then(({ data }) => {
                commit(types.RECEIVE_OS_DICT, data);
            });
    },
};

const mutations = {
    [types.RECEIVE_CONFIG] (state, response) {
        state.config = response.data;
        state.apiVersion = response.meta.api_version;
    },
    [types.RECEIVE_CONFIG_THEME] (state, data) {
        const theme = data?.upyield?.ui?.theme?.selected;
        if (theme) {
            set(state.config, 'upyield.ui.theme.selected', theme);
        }
        if (data?.feature) {
            set(state.config, 'feature', data.feature);
        }
        state.config = { ...state.config };
    },
    [types.RECEIVE_ADVERTISERS_DICTIONARY] (state, { data }) {
        state.advertisersDictionary = data.map(
            item => Object.assign({}, { id: item.id, short: item.short, name: item.name, paused: item.paused })
        );
    },
    [types.RECEIVE_PARTNERS_DICTIONARY] (state, { data }) {
        state.partnersDictionary = data.map(
            item => Object.assign({}, { id: item.id, short: item.short, legal: item.legal, paused: item.paused })
        );
    },
    [types.RECEIVE_USERS_DICTIONARY] (state, { data }) {
        state.usersDictionary = data;
    },
    [types.RECEIVE_USER_ROLES_DICTIONARY] (state, { data }) {
        state.userRolesDictionary = data;
    },
    [types.RECEIVE_EVENT_TYPES_DICT] (state, { data }) {
        state.eventTypesDict = data;
        state.eventTypesWithAllOptionDict = data.length
            ? [{ string_id: null, name: 'All' }, ...data ]
            : []
        ;
    },
    [types.RECEIVE_EVENT_TYPES_EXTENDED_DICT] (state, { data }) {
        state.eventTypesExtendedDict = data;
    },
    [types.RECEIVE_VIRTUAL_EVENT_TYPES] (state, { data }) {
        state.virtualEventTypes = data;
    },
    [types.RECEIVE_TIMEZONES_DICT] (state, { data }) {
        state.timezonesDict = data;
    },
    [types.RECEIVE_COUNTRIES_DICT] (state, { data }) {
        state.countries = data;
        state.countriesWithAllOption = data.length
            ? [{ '.type': 'country', code: 'ALL', display_name: 'ALL', name: 'ALL' }, ...data ]
            : []
        ;
    },
    [types.RECEIVE_TRACKING_PARAMS_DICT] (state, { data }) {
        state.trackingParams = data;
    },
    [types.RECEIVE_IMPRESSION_TRACKING_PARAMS_DICT] (state, { data }) {
        state.impressionTrackingParams = data;
    },
    [types.RECEIVE_CLICKTAG_PARAMS_DICT] (state, { data }) {
        state.clicktagParams = data;
    },
    [types.RECEIVE_CATEGORIES_DICT] (state, { data }) {
        state.categories = data;
    },
    [types.RECEIVE_CURRENCIES_DICT] (state, { data }) {
        state.currenciesDict = data;
    },
    [types.RECEIVE_OS_DICT] (state, { data }) {
        state.osDict = data.map(os => ({ ...os, display_name: os.value }));
    },
    [types.RECEIVE_CURRENCIES_DICT_TIMESTAMP] (state, timestamp) {
        state.currenciesDictTimestampMinutes = timestamp;
    },
    [types.RECEIVE_EVENT_TYPES_DICT_TIMESTAMP] (state, timestamp) {
        state.eventTypesDictTimestampMinutes = timestamp;
    },
    [types.RECEIVE_EVENT_TYPES_EXTENDED_DICT_TIMESTAMP] (state, timestamp) {
        state.eventTypesExtendedDictTimestampMinutes = timestamp;
    },
    [types.RECEIVE_ISPS_DICTIONARY] (state, { data }) {
        state.ISPsDictionary = data;
    },
    [types.RECEIVE_FRAUD_REASONS_DICT] (state, { data }) {
        state.fraudReasons = data;
    },
    [types.RECEIVE_DICTIONARIES] (state, response) {
        state.paymentTerms = response.data.partners_payment_terms || [];
        state.trafficTypes = response.data.traffic_types || [];
        state.partnersTrafficTypes = response.data.partners_traffic_types || [];
        state.partnersTypes = response.data.partners_types || [];
        state.trafficSources = response.data.partners_types || [];
        state.postbackEvents = response.data.postbacks_events_def || [];
        state.trafficSplitterParams = response.data.trafficsplitter_params_def || [];
        state.tagTypes = response.data.tags_type_def || [];
        state.statusesOnboarding = response.data.onboarding_status_types || [];
        state.carriers = response.data.carriers || [];
        state.iabCategories = response.data.iab_categories || {};
        state.iabCategoryNames = response.data.iab_category_names || [];
        state.emailEvents = (response.data.email_event_name || []).sort((a, b) => a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1);
        state.capTypes = response.data.cap_types.map((capType) => ({ id: capType.code, ...capType }));
        state.payoutTypes = response.data.payout_types || [];
        state.activityLogOperations = response.data.audit_log_operations || [];
        state.splittersTypes = response.data.splitters_types || [];
        state.deviceTypes = response.data.device_types || [];
        state.advertiserPostbackEvents = response.data.advertiser_postback_events || [];
        state.postbackReportStatuses = response.data.postback_report_statuses || [];
        state.eventStatusChangeTypes = Object.entries(response.data.event_status_change_types || {}).reduce((newArr, type) => {
            newArr.push({ id: type[0], name: type[1] });
            return newArr;
        }, []) || [];
        state.approvalEventStatuses = response.data.approval_event_statuses || [];
        state.eventErrorCodes = response.data.event_error_codes || [];
        state.advertiserPostbackStatuses = response.data.advertiser_postback_statuses || [];
        state.clickDataTypes = response.data.click_data_types || [];
        state.applicationPostbackMethods = response.data.application_postback_methods.map((method) => ({ ...method, disabled: !method.enabled })) || [];
        state.whitelistIpTypes = response.data.whitelist_ip_types || [];
        state.advertiserGeneratorParams = response.data.advertiser_generator_params || [];
        state.offerCreatedTimeRange = response.data.offer_created_time_range || [];
        state.analyticsKPITypes = response.data.analytics_kpi_types || [];
        state.extCampaignStatuses = response.data.ext_campaign_statuses || [];
        state.extCampaignTypes = response.data.ext_campaign_types || [];
        state.revenuesGroups = response.data.revenues_groups || [];
        state.postbackBodyTypes = response.data.postback_body_types || [];
        state.foreignLeadSources = response.data.foreign_lead_sources || [];
    },
};

export default {
    actions,
    getters,
    mutations,
    state,
};
